import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';

type GenerationType = { contentType: string; schemaVersion: string };
type GenerationJob = {
  jobId: string;
  status: string;
  stage: string;
  contentType: string;
  schemaVersion: string;
  promptPreview?: string;
  prompt?: string;
  createdAt?: string;
  startedAt?: string;
  updatedAt?: string;
  completedAt?: string;
  errorCode?: string;
  errorMessage?: string;
  model?: string;
  providerName?: string;
  storageObjectKey?: string;
  reviewOutcome?: string;
  catalogContentId?: number;
  catalogStatus?: string;
  contentReady?: boolean;
  canDeleteFile?: boolean;
  canImport?: boolean;
  canReject?: boolean;
  canRetry?: boolean;
  canDelete?: boolean;
  canCancel?: boolean;
  canReimport?: boolean;
};

type LabBlock = { type?: string; content?: string; language?: string; url?: string; altText?: string; displayOrder?: number };
type LabStep = { title?: string; displayOrder?: number; expectedResult?: string; notes?: string; blocks?: LabBlock[] };
type LabDraft = { name?: string; subtitle?: string; description?: string; technologyName?: string; difficulty?: string; duration?: string; prerequisites?: string; learningOutcomes?: string; steps?: LabStep[] };
type CourseTopic = { title?: string; displayOrder?: number; objective?: string; estimatedMinutes?: number; blocks?: LabBlock[] };
type CourseModule = {
  title?: string;
  displayOrder?: number;
  goal?: string;
  topics?: CourseTopic[];
  assessment?: { title?: string; passingScore?: number; questions?: { prompt?: string }[] };
  lab?: { name?: string; description?: string; steps?: LabStep[] };
};
type CourseDraft = {
  title?: string;
  subtitle?: string;
  summary?: string;
  learningOutcomes?: string;
  prerequisites?: string;
  difficulty?: string;
  estimatedDurationHours?: number;
  tags?: string[];
  modules?: CourseModule[];
};

const terminal = (status?: string) => status === 'Completed' || status === 'Failed';

const ContentGeneratorPage: React.FC = () => {
  const [types, setTypes] = useState<GenerationType[]>([]);
  const [contentType, setContentType] = useState('Lab');
  const [schemaVersion, setSchemaVersion] = useState('v1');
  const [prompt, setPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState<GenerationJob | null>(null);
  const [draft, setDraft] = useState<LabDraft | CourseDraft | null>(null);
  const [rawJson, setRawJson] = useState('');
  const [history, setHistory] = useState<GenerationJob[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);

  const loadHistory = () => {
    contentApi.generationJobs({ page: 1, pageSize: 20 })
      .then((response) => setHistory(response.data.jobs || []))
      .catch(() => undefined);
  };

  useEffect(() => {
    contentApi.generationTypes()
      .then((response) => {
        const rows = response.data.types || [];
        setTypes(rows);
        if (rows[0]) {
          setContentType(rows[0].contentType);
          setSchemaVersion(rows[0].schemaVersion);
        }
      })
      .catch(() => setError('Supported content types could not be loaded.'));
    loadHistory();
  }, []);

  useEffect(() => {
    if (!job?.jobId || terminal(job.status)) return undefined;
    let stopped = false;
    let timer = 0;
    const tick = () => {
      contentApi.generationJob(job.jobId)
        .then((response) => {
          if (stopped) return;
          const next = response.data.job as GenerationJob;
          setJob(next);
          if (!terminal(next.status)) {
            timer = window.setTimeout(tick, 2000);
          } else {
            loadHistory();
          }
        })
        .catch((err: unknown) => {
          if (!stopped) setError(err instanceof Error ? err.message : 'Status could not be refreshed.');
        });
    };
    timer = window.setTimeout(tick, 2000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [job?.jobId, job?.status]);

  useEffect(() => {
    if (!job?.jobId || !job.contentReady) {
      setDraft(null);
      setRawJson('');
      return undefined;
    }
    let stopped = false;
    contentApi.generationContent(job.jobId)
      .then((response) => {
        if (stopped) return;
        setDraft(response.data as LabDraft);
        setRawJson(JSON.stringify(response.data, null, 2));
      })
      .catch((err: unknown) => {
        if (!stopped) setError(err instanceof Error ? err.message : 'Generated content could not be loaded.');
      });
    return () => {
      stopped = true;
    };
  }, [job?.jobId, job?.contentReady, job?.updatedAt]);

  const generate = async () => {
    setError(null);
    setMessage(null);
    if (!prompt.trim()) {
      setError('Enter a prompt.');
      return;
    }
    if (!idempotencyKey.current) idempotencyKey.current = crypto.randomUUID();
    setSubmitting(true);
    try {
      const response = await contentApi.createGeneration(
        { prompt: prompt.trim(), contentType, schemaVersion },
        idempotencyKey.current
      );
      idempotencyKey.current = null;
      const created = await contentApi.generationJob(response.data.jobId);
      setJob(created.data.job);
      setMessage('Generation started.');
      loadHistory();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Generation could not be started.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeJob = () => {
    setJob(null);
    setDraft(null);
    setRawJson('');
    setError(null);
  };

  const openJob = async (jobId: string) => {
    setError(null);
    const response = await contentApi.generationJob(jobId);
    setJob(response.data.job);
  };

  const download = async () => {
    if (!job) return;
    const response = await contentApi.downloadGeneration(job.jobId);
    const blob = new Blob([response.data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'content.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const deleteJob = async () => {
    if (!job) return;
    if (job.reviewOutcome !== 'Imported') {
      const subject = job.contentType === 'Course' ? 'This course has not been imported.' : 'This draft has not been imported.';
      if (!window.confirm(`${subject} Delete this job and its stored file?`)) return;
    }
    setError(null);
    try {
      const response = await contentApi.deleteGeneration(job.jobId);
      setJob(null);
      setDraft(null);
      setRawJson('');
      setMessage(response.data.message || 'Job deleted.');
      loadHistory();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The job could not be deleted.');
    }
  };

  const reimportJob = () => {
    if (!job) return;
    if (!window.confirm('Reimport deletes the current draft and its labs, then creates them again. Continue?')) return;
    runAction(() => contentApi.reimportGeneration(job.jobId), 'Draft replaced. Nothing is published.');
  };

  const runAction = async (action: () => Promise<unknown>, success: string) => {
    if (!job) return;
    setError(null);
    try {
      await action();
      const response = await contentApi.generationJob(job.jobId);
      setJob(response.data.job);
      setMessage(success);
      loadHistory();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The action failed.');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <Link to="/admin/dashboard?tab=courses" className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to courses
      </Link>
      <h1 className="mt-2 text-3xl font-bold">AI Content Generator</h1>
      <p className="mt-2 text-gray-600">
        {contentType === 'Course'
          ? 'A course is built in the background: one outline, then one module at a time. The stage shows the current step. A long outline is grouped into a few topics per module. Nothing is published until review.'
          : 'Generate a reviewable draft. Nothing is published until you send the catalog item through the normal review flow.'}
      </p>
      <div className="mt-6 grid gap-4 md:grid-cols-[16rem_1fr]">
        <label className="block text-sm font-semibold text-gray-900">
          Content type
          <select
            className="mt-1.5 w-full rounded-md border-2 border-gray-300 bg-white px-3 py-2.5"
            value={`${contentType}:${schemaVersion}`}
            onChange={(event) => {
              const [type, version] = event.target.value.split(':');
              setContentType(type);
              setSchemaVersion(version);
            }}
          >
            {types.map((type) => (
              <option key={`${type.contentType}:${type.schemaVersion}`} value={`${type.contentType}:${type.schemaVersion}`}>
                {type.contentType} {type.schemaVersion}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-semibold text-gray-900">
          Prompt
          <textarea
            className="mt-1.5 w-full rounded-md border-2 border-gray-300 bg-white px-3 py-2.5 min-h-28"
            placeholder={contentType === 'Course' ? 'Create an advanced Python course' : 'Describe the lab you want generated'}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
          />
        </label>
      </div>
      <Button type="button" onClick={generate} disabled={submitting || types.length === 0} isLoading={submitting}>
        Generate
      </Button>
      <ActionStatus message={message} error={error} />

      {job && (
        <section className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">{job.contentType} draft</h2>
              <p className="text-sm text-gray-600">Stage: {job.stage} · Schema {job.schemaVersion}</p>
              {job.model && <p className="text-sm text-gray-500">{job.providerName} · {job.model}</p>}
              {job.storageObjectKey && <p className="text-sm text-gray-500">Stored as {job.storageObjectKey}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              {job.contentReady && job.storageObjectKey && <Button type="button" variant="outline" onClick={download}>Download JSON</Button>}
              {job.canImport && (
                <Button type="button" onClick={() => runAction(() => contentApi.importGeneration(job.jobId), 'Imported as a catalog draft.')}>
                  Import as draft
                </Button>
              )}
              {job.canReimport && (
                <Button type="button" onClick={reimportJob}>
                  Reimport
                </Button>
              )}
              {job.canReject && (
                <Button type="button" variant="outline" onClick={() => runAction(() => contentApi.rejectGeneration(job.jobId), 'Rejected and the stored file was removed.')}>
                  Reject
                </Button>
              )}
              {job.canDeleteFile && (
                <Button type="button" variant="outline" onClick={() => runAction(() => contentApi.deleteGenerationFile(job.jobId), 'Stored file removed.')}>
                  Delete stored file
                </Button>
              )}
              {job.canRetry && (
                <Button type="button" onClick={() => runAction(() => contentApi.retryGeneration(job.jobId), 'Generation queued again.')}>
                  Try again
                </Button>
              )}
              {job.canCancel && (
                <Button type="button" variant="outline" onClick={() => runAction(() => contentApi.cancelGeneration(job.jobId), 'Generation cancelled. You can delete the job.')}>
                  Cancel job
                </Button>
              )}
              {job.canDelete && (
                <Button type="button" variant="outline" onClick={deleteJob}>
                  {job.reviewOutcome === 'Imported' && job.catalogStatus === 'Draft' ? 'Delete job and draft' : 'Delete job'}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={closeJob}>Close</Button>
            </div>
          </div>
          {job.errorMessage && <p className="mt-3 whitespace-pre-wrap text-sm text-red-700">{job.errorCode}: {job.errorMessage}</p>}
          {job.catalogContentId && job.contentType === 'Lab' && (
            <p className="mt-3 text-sm">
              Catalog draft #{job.catalogContentId} ({job.catalogStatus || 'Draft'}).{' '}
              <Link className="text-primary-700 underline" to={`/admin/labs/${job.catalogContentId}/edit`}>Open lab editor</Link>
            </p>
          )}
          {job.catalogContentId && job.contentType === 'Course' && (
            <p className="mt-3 text-sm">
              Catalog draft #{job.catalogContentId} ({job.catalogStatus || 'Draft'}). Module labs were created as drafts too.{' '}
              <Link className="text-primary-700 underline" to={`/my-courses/${job.catalogContentId}/edit`}>Open course editor</Link>
            </p>
          )}
          {draft && job.contentType === 'Course' && <CoursePreview draft={draft as CourseDraft} rawJson={rawJson} />}
          {draft && job.contentType !== 'Course' && <LabPreview draft={draft as LabDraft} rawJson={rawJson} />}
        </section>
      )}

      <section className="mt-10">
        <h2 className="text-xl font-semibold">Recent jobs</h2>
        <ul className="mt-3 space-y-2">
          {history.map((item) => (
            <li key={item.jobId}>
              <button type="button" className="w-full rounded-lg border border-gray-200 px-4 py-3 text-left hover:bg-gray-50" onClick={() => openJob(item.jobId)}>
                <span className="font-medium">{item.contentType}</span>
                <span className="ml-2 text-sm text-gray-500">{item.status}</span>
                <p className="text-sm text-gray-600 truncate">{item.promptPreview}</p>
              </button>
            </li>
          ))}
          {history.length === 0 && <li className="text-sm text-gray-500">No generation jobs yet.</li>}
        </ul>
      </section>
    </div>
  );
};

const BlockList: React.FC<{ blocks?: LabBlock[] }> = ({ blocks }) => (
  <>
    {(blocks || []).map((block, index) => (
      <div key={`${block.displayOrder ?? index}`} className="mt-3 text-sm text-gray-800">
        <p className="text-xs uppercase tracking-wide text-gray-500">{block.type}</p>
        {block.language && <p className="text-xs text-gray-500">{block.language}</p>}
        <pre className="whitespace-pre-wrap font-sans">{block.content}</pre>
        {block.url && <p className="text-primary-700">{block.url}</p>}
        {block.type === 'Image' && <p className="text-gray-500">Diagram required: {block.altText}</p>}
      </div>
    ))}
  </>
);

const LabPreview: React.FC<{ draft: LabDraft; rawJson: string }> = ({ draft, rawJson }) => (
  <div className="mt-6 space-y-4">
    <div>
      <h3 className="text-2xl font-semibold">{draft.name}</h3>
      {draft.subtitle && <p className="text-gray-600">{draft.subtitle}</p>}
      <p className="mt-2 text-gray-800">{draft.description}</p>
      <p className="mt-2 text-sm text-gray-600">{draft.technologyName} · {draft.difficulty} · {draft.duration}</p>
      {draft.prerequisites && <p className="mt-2 text-sm"><span className="font-medium">Prerequisites: </span>{draft.prerequisites}</p>}
      {draft.learningOutcomes && <p className="mt-1 text-sm"><span className="font-medium">Outcomes: </span>{draft.learningOutcomes}</p>}
    </div>
    {(draft.steps || []).map((step) => (
      <article key={step.displayOrder} className="rounded-lg border border-gray-100 p-4">
        <h4 className="font-semibold">{step.displayOrder}. {step.title}</h4>
        <BlockList blocks={step.blocks} />
        {step.expectedResult && <p className="mt-3 text-sm"><span className="font-medium">Expected result: </span>{step.expectedResult}</p>}
        {step.notes && <p className="mt-1 text-sm text-gray-600">{step.notes}</p>}
      </article>
    ))}
    <JsonPreview rawJson={rawJson} />
  </div>
);

const CoursePreview: React.FC<{ draft: CourseDraft; rawJson: string }> = ({ draft, rawJson }) => (
  <div className="mt-6 space-y-4">
    <div>
      <h3 className="text-2xl font-semibold">{draft.title}</h3>
      {draft.subtitle && <p className="text-gray-600">{draft.subtitle}</p>}
      <p className="mt-2 text-gray-800">{draft.summary}</p>
      <p className="mt-2 text-sm text-gray-600">{draft.difficulty} · {draft.estimatedDurationHours} hours{(draft.tags || []).length > 0 ? ` · ${draft.tags!.join(', ')}` : ''}</p>
      {draft.prerequisites && <p className="mt-2 text-sm"><span className="font-medium">Prerequisites: </span>{draft.prerequisites}</p>}
      {draft.learningOutcomes && <p className="mt-1 text-sm"><span className="font-medium">Outcomes: </span>{draft.learningOutcomes}</p>}
    </div>
    {(draft.modules || []).map((module) => (
      <article key={module.displayOrder} className="rounded-lg border border-gray-200 p-4 space-y-4">
        <div>
          <h4 className="text-lg font-semibold">{module.displayOrder}. {module.title}</h4>
          {module.goal && <p className="text-sm text-gray-600">{module.goal}</p>}
        </div>
        {(module.topics || []).map((topic) => (
          <section key={topic.displayOrder}>
            <h5 className="font-medium">{topic.displayOrder}. {topic.title}</h5>
            {topic.objective && <p className="text-sm text-gray-600">{topic.objective}</p>}
            <BlockList blocks={topic.blocks} />
          </section>
        ))}
        {module.assessment && (
          <p className="text-sm"><span className="font-medium">Assessment: </span>{module.assessment.title} · {(module.assessment.questions || []).length} questions</p>
        )}
        {module.lab && (
          <section className="rounded-md bg-gray-50 p-3">
            <p className="text-sm font-medium">Lab: {module.lab.name}</p>
            {module.lab.description && <p className="text-sm text-gray-700">{module.lab.description}</p>}
            <p className="text-xs text-gray-500">{(module.lab.steps || []).length} steps</p>
          </section>
        )}
      </article>
    ))}
    <JsonPreview rawJson={rawJson} />
  </div>
);

const JsonPreview: React.FC<{ rawJson: string }> = ({ rawJson }) => (
  <details>
    <summary className="cursor-pointer text-sm font-medium text-gray-700">JSON preview</summary>
    <pre className="mt-2 overflow-x-auto rounded-lg bg-gray-950 p-4 text-xs text-gray-100">{rawJson}</pre>
  </details>
);

export default ContentGeneratorPage;
